/**
 * Converts a flat path string into a nested error object structure compatible with React Hook Form.
 *
 * React Hook Form expects errors to be nested according to the field path. For example,
 * a field path like "events.primary[0].eventType" needs an error structure like:
 * ```javascript
 * {
 *   events: {
 *     primary: [
 *       {
 *         eventType: { message: 'Error message' }
 *       }
 *     ]
 *   }
 * }
 * ```
 *
 * This function mutates the provided `errors` object to add the error at the correct nested path.
 *
 * @param {Object} errors - The errors object to mutate. This object will be modified in place.
 * @param {string} path - The field path in dot notation, optionally with array indices in brackets (e.g., "events.primary[0].eventType").
 * @param {string} message - The error message to set at the specified path.
 *
 * @example
 * // Simple field
 * const errors = {}
 * setNestedError(errors, 'name', 'Name is required')
 * // Result: { name: { message: 'Name is required' } }
 *
 * @example
 * // Nested field
 * const errors = {}
 * setNestedError(errors, 'person.firstName', 'First name is required')
 * // Result: { person: { firstName: { message: 'First name is required' } } }
 *
 * @example
 * // Array index
 * const errors = {}
 * setNestedError(errors, 'events[0].type', 'Type is required')
 * // Result: { events: [{ type: { message: 'Type is required' } }] }
 *
 * @example
 * // Complex nested path with arrays
 * const errors = {}
 * setNestedError(errors, 'events.primary[0].eventType', 'Event type is required')
 * // Result: { events: { primary: [{ eventType: { message: 'Event type is required' } }] } }
 */
export const setNestedError = (errors, path, message) => {
  if (!path) return
  const segments = path
    .replaceAll(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)
  let current = errors

  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1
    const nextIsIndex = Number.isInteger(Number(segments[index + 1]))

    if (isLast) {
      current[segment] = { message }
      return
    }

    if (!current[segment]) {
      current[segment] = nextIsIndex ? [] : {}
    }
    current = current[segment]
  })
}

/**
 * Helper function to recursively check if any value in a nested structure is true.
 * Used internally by hasAnyDirtyField.
 *
 * @private
 * @param {*} value - The value to check
 * @returns {boolean} true if any nested value is true
 */
const checkAnyTrue = (value) => {
  if (value === true) return true
  if (value === false || value === null || value === undefined) return false

  if (Array.isArray(value)) {
    return value.some(checkAnyTrue)
  }

  if (typeof value === 'object') {
    return Object.values(value).some(checkAnyTrue)
  }

  return false
}

/**
 * Gets a value from a nested object using dot notation path with array indices.
 * Useful for accessing React Hook Form's dirtyFields object.
 *
 * When using `mode: 'all'` in react-hook-form, dirtyFields contains:
 * - `true` for dirty (modified) fields
 * - `false` for pristine (unmodified) fields
 * - Nested objects/arrays that mirror the form structure
 *
 * @param {Object} obj - The object to traverse (e.g., dirtyFields from React Hook Form)
 * @param {string} path - The field path in dot notation, optionally with array indices in brackets (e.g., "events.primary[0].eventType")
 * @returns {*} The value at the specified path, or undefined if the path doesn't exist
 *
 * @example
 * // Simple nested field
 * const dirtyFields = { events: { primary: [{ eventType: true }] } }
 * getNestedValue(dirtyFields, 'events.primary[0].eventType')
 * // => true
 *
 * @example
 * // Check if a relationship field is dirty
 * getNestedValue(dirtyFields, 'events.primary[0].relationship.otherPerson')
 * // => true (if dirty), false (if pristine), or undefined (if path doesn't exist)
 *
 * @example
 * // Get the entire dirty state for an array element
 * getNestedValue(dirtyFields, 'events.additional[1]')
 * // => { eventType: true, dates: [{ value: false }], places: [{ value: true }] }
 */
export const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined

  const segments = path
    .replaceAll(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)

  return segments.reduce((current, segment) => {
    // If we hit a true value (parent is dirty), return true early
    if (current === true) return true
    if (current === null || current === undefined) return undefined
    return current[segment]
  }, obj)
}

/**
 * Checks if a field is dirty in React Hook Form's dirtyFields object.
 * Returns true only if the field is explicitly marked as dirty (value === true).
 *
 * This is a convenience wrapper around getNestedValue specifically for checking dirty state.
 *
 * @param {Object} dirtyFields - The dirtyFields object from react-hook-form's formState
 * @param {string} path - The field path in dot notation (e.g., "events.primary[0].relationship.otherPerson")
 * @returns {boolean} true if the field is dirty, false otherwise
 *
 * @example
 * // Check if a specific field is dirty
 * isDirtyPath(dirtyFields, 'events.primary[0].eventType')
 * // => true (if modified), false (if pristine or doesn't exist)
 *
 * @example
 * // Check if any field in a nested object is dirty
 * isDirtyPath(dirtyFields, 'events.primary[0].relationship')
 * // => could return an object like { otherPerson: true, data: false }
 * // Use hasAnyDirtyField() instead if you want to check if ANY nested field is dirty
 */
export const isDirtyPath = (dirtyFields, path) => {
  const value = getNestedValue(dirtyFields, path)
  return value === true
}

/**
 * Recursively checks if any nested field within an object/array is marked as dirty (true).
 * Useful for checking if ANY field within a complex nested structure has been modified.
 *
 * @param {Object} dirtyFields - The dirtyFields object from react-hook-form's formState
 * @param {string} [path] - The field path in dot notation. If omitted, checks the entire dirtyFields object.
 * @returns {boolean} true if any nested field is dirty, false otherwise
 *
 * @example
 * // Check if any field in an event object is dirty
 * hasAnyDirtyField(dirtyFields, 'events.primary[0]')
 * // => true if eventType, dates, places, or any nested field is dirty
 *
 * @example
 * // Check if any date field is dirty
 * hasAnyDirtyField(dirtyFields, 'events.primary[0].dates')
 * // => true if any date in the array has been modified
 *
 * @example
 * // Check if the entire object has any dirty fields (no path)
 * hasAnyDirtyField(eventDirtyFields)
 * // => true if any field in the object is dirty
 */
export const hasAnyDirtyField = (dirtyFields, path) => {
  // If no path provided, check the dirtyFields object directly
  if (!path) {
    return checkAnyTrue(dirtyFields)
  }

  const value = getNestedValue(dirtyFields, path)
  return checkAnyTrue(value)
}

/**
 * Recursively touches only fields that have validation errors.
 * This is useful when you want validation errors to display without using handleSubmit
 * (which validates the entire form) and without affecting dirty state (used for tracking changes).
 *
 * React Hook Form only displays errors on fields that have been "touched" (interacted with).
 * This function selectively marks only fields with errors as touched, ensuring validation
 * errors appear while preserving the dirty state needed for change tracking.
 *
 * IMPORTANT: Call trigger() first to validate, then call this function to touch error fields.
 *
 * @param {Object} errors - The errors object from react-hook-form's formState
 * @param {Function} setValue - The setValue function from react-hook-form
 * @param {Function} getValues - The getValues function from react-hook-form
 * @param {string} [basePath=''] - The base path to prepend to field paths
 *
 * @example
 * // Touch only fields with errors after validation
 * const isValid = await trigger('events.primary')
 * if (!isValid) {
 *   touchFieldsWithErrors(errors.events?.primary, setValue, getValues, 'events.primary')
 * }
 *
 * @example
 * // Touch error fields at root level
 * await trigger()
 * touchFieldsWithErrors(errors, setValue, getValues)
 */
export const touchFieldsWithErrors = (errors, setValue, getValues, basePath = '') => {
  if (!errors || typeof errors !== 'object') {
    return
  }

  Object.keys(errors).forEach((key) => {
    const error = errors[key]
    const fieldPath = basePath ? `${basePath}.${key}` : key

    if (Array.isArray(error)) {
      // Handle array fields (e.g., events.primary[0])
      error.forEach((item, idx) => {
        if (item && typeof item === 'object') {
          touchFieldsWithErrors(item, setValue, getValues, `${fieldPath}[${idx}]`)
        }
      })
    } else if (error && typeof error === 'object' && error.message) {
      // This is a leaf error with a message - touch this field
      const currentValue = getValues(fieldPath)
      setValue(fieldPath, currentValue, { shouldTouch: true, shouldValidate: false })
    } else if (error && typeof error === 'object') {
      // Nested error object - recurse
      touchFieldsWithErrors(error, setValue, getValues, fieldPath)
    }
  })
}
